import { useState, useEffect } from 'react';

export default function StockHistory({ apiUrl }) {
  // ต้นทุนเป็นข้อมูลของเจ้าของร้านเท่านั้น — ดู StockManagement.jsx สำหรับเหตุผลเดียวกัน
  const isBranchManager = localStorage.getItem('adminRole') === 'manager';
  const [transactions, setTransactions] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    menuItemId: '',
    type: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const menuRes = await fetch(`${apiUrl}/menu`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const menuData = await menuRes.json();
      setMenuItems(menuData.data || menuData);
    } catch (error) {
      console.error('Failed to load menu:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const params = new URLSearchParams();
      if (filters.menuItemId) params.append('menuItemId', filters.menuItemId);
      if (filters.type) params.append('type', filters.type);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(`${apiUrl}/api/stock/transactions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setTransactions(data.data || []);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = isBranchManager
      ? ['วันที่', 'ประเภท', 'เมนู', 'จำนวน', 'ผู้จัดจำหน่าย', 'หมายเหตุ']
      : ['วันที่', 'ประเภท', 'เมนู', 'จำนวน', 'ต้นทุน/หน่วย', 'รวม', 'ผู้จัดจำหน่าย', 'หมายเหตุ'];
    const rows = transactions.map(tx => {
      const row = [
        new Date(tx.createdAt).toLocaleString('th-TH'),
        tx.displayType,
        tx.menuItem?.name || '-',
        `${tx.quantity.toFixed(2)} ${tx.menuItem?.unit || ''}`
      ];
      if (!isBranchManager) row.push(tx.unitCost.toFixed(2), tx.totalCost.toFixed(2));
      row.push(tx.supplier || '-', tx.notes || '-');
      return row;
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `stock_history_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'purchase': return '#27ae60';
      case 'sale': return '#e74c3c';
      case 'adjustment': return '#f39c12';
      case 'return': return '#2980b9';
      case 'issue': return '#8e44ad';
      default: return '#95a5a6';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'purchase': return '🟢';
      case 'sale': return '🔴';
      case 'adjustment': return '🟡';
      case 'return': return '🔵';
      case 'issue': return '📤';
      default: return '⚪';
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>📊 ประวัติการเคลื่อนไหวสต็อก</h2>
        <button onClick={handleExportCSV} className="button">
          📤 ส่งออก CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="row">
          <div className="col-md-3">
            <div className="form-group">
              <label>เมนู</label>
              <select
                value={filters.menuItemId}
                onChange={(e) => setFilters({ ...filters, menuItemId: e.target.value })}
              >
                <option value="">ทั้งหมด</option>
                {menuItems.map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label>ประเภท</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <option value="">ทั้งหมด</option>
                <option value="purchase">ซื้อเข้า</option>
                <option value="sale">ขายออก</option>
                <option value="adjustment">ปรับปรุง</option>
                <option value="return">คืนสต็อก</option>
                <option value="issue">เบิกใช้</option>
              </select>
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label>วันที่เริ่มต้น</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>
          </div>

          <div className="col-md-3">
            <div className="form-group">
              <label>วันที่สิ้นสุด</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div style={{ overflowX: 'auto' }}>
          <table className="table-nowrap">
            <thead>
              <tr>
                <th>วันที่-เวลา</th>
                <th>ประเภท</th>
                <th>เมนู</th>
                <th>จำนวน</th>
                {!isBranchManager && <th>ต้นทุน/หน่วย</th>}
                {!isBranchManager && <th>รวม</th>}
                <th>ผู้จัดจำหน่าย/หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, index) => (
                <tr key={tx.id || index}>
                  <td>{new Date(tx.createdAt).toLocaleString('th-TH')}</td>
                  <td>
                    <span style={{ color: getTypeColor(tx.type), fontWeight: '600' }}>
                      {getTypeIcon(tx.type)} {tx.displayType}
                    </span>
                  </td>
                  <td>{tx.menuItem?.name || '-'}</td>
                  <td>
                    <span style={{
                      color: tx.quantity >= 0 ? '#27ae60' : '#e74c3c',
                      fontWeight: '600'
                    }}>
                      {tx.quantity >= 0 ? '+' : ''}{tx.quantity.toFixed(2)} {tx.menuItem?.unit || ''}
                    </span>
                  </td>
                  {!isBranchManager && <td>{tx.unitCost.toFixed(2)} ฿</td>}
                  {!isBranchManager && (
                    <td>
                      <span style={{ fontWeight: '600' }}>
                        {tx.totalCost.toFixed(2)} ฿
                      </span>
                    </td>
                  )}
                  <td style={{ fontSize: '0.9rem', color: '#888' }}>
                    {tx.supplier && <div>📦 {tx.supplier}</div>}
                    {tx.invoiceNumber && <div>🧾 {tx.invoiceNumber}</div>}
                    {tx.notes && <div>📝 {tx.notes}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {transactions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              ไม่พบข้อมูลการเคลื่อนไหว
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
